import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  maxWidth?: string;
  confirmClose?: boolean; // Warn user if they have unsaved changes

  /** Custom confirm handler for styled dialogs. If provided, called instead of window.confirm() */
  onConfirmClose?: () => Promise<boolean>;

  /** Custom z-index for nested modals (default: 1000, use 10000 for PhotoModal-style overlays) */
  zIndex?: number;

  /** Whether to show the close button (default: true) */
  showCloseButton?: boolean;

  /** Custom header content for complex layouts (overrides title rendering) */
  headerContent?: React.ReactNode;

  /** Accessible label when using headerContent without a title (default: 'Dialog') */
  ariaLabel?: string;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = '800px',
  confirmClose = false,
  onConfirmClose,
  zIndex = 1000,
  showCloseButton = true,
  headerContent,
  ariaLabel,
}) => {
  const { isMobile } = useMediaQuery();
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const mouseDownOnBackdrop = useRef<boolean>(false);
  const isConfirmPending = useRef<boolean>(false);

  // Save focus when modal opens, restore when it closes (WCAG 2.1 Level A requirement)
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement as HTMLElement;
    } else if (previousActiveElement.current) {
      // Return focus to trigger element after modal closes
      previousActiveElement.current.focus();
    }
  }, [isOpen]);

  // Handle Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = async (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Only handle if this modal has focus (prevents closing nested modals)
        if (!modalRef.current?.contains(document.activeElement)) {
          return;
        }

        if (confirmClose) {
          if (isConfirmPending.current) return; // Guard against re-entry
          e.preventDefault();
          isConfirmPending.current = true;
          try {
            const shouldClose = onConfirmClose
              ? await onConfirmClose()
              : window.confirm('You have unsaved changes. Are you sure you want to close?');
            if (shouldClose) {
              onClose();
            }
          } finally {
            isConfirmPending.current = false;
          }
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, confirmClose, onConfirmClose, onClose]);

  // Prevent body scroll when modal is open
  // scrollbar-gutter: stable on html prevents layout shift when scrollbar disappears
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus trap - focus first input when modal opens
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const modal = modalRef.current;

    const getFocusableElements = () => {
      return modal.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
      );
    };

    const handleKeyDown = (e: Event) => {
      const keyboardEvent = e as KeyboardEvent;
      if (keyboardEvent.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      if (keyboardEvent.shiftKey && document.activeElement === firstElement) {
        keyboardEvent.preventDefault();
        lastElement.focus();
      } else if (!keyboardEvent.shiftKey && document.activeElement === lastElement) {
        keyboardEvent.preventDefault();
        firstElement.focus();
      }
    };

    // Focus first element after render cycle completes
    requestAnimationFrame(() => {
      const focusableElements = getFocusableElements();
      if (focusableElements.length > 0) {
        (focusableElements[0] as HTMLElement).focus();
      }
    });

    modal.addEventListener('keydown', handleKeyDown);
    return () => modal.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Handle close with optional confirmation
  const handleClose = async () => {
    if (confirmClose) {
      if (isConfirmPending.current) return;
      isConfirmPending.current = true;
      try {
        const shouldClose = onConfirmClose
          ? await onConfirmClose()
          : window.confirm('You have unsaved changes. Are you sure you want to close?');
        if (shouldClose) {
          onClose();
        }
      } finally {
        isConfirmPending.current = false;
      }
    } else {
      onClose();
    }
  };

  // Track mouse-down on backdrop to prevent accidental closes during text selection
  const handleBackdropMouseDown = () => {
    mouseDownOnBackdrop.current = true;
  };

  // Handle backdrop mouse-up (only close if mouse-down also occurred on backdrop)
  const handleBackdropMouseUp = () => {
    if (mouseDownOnBackdrop.current) {
      handleClose();
    }
    mouseDownOnBackdrop.current = false;
  };

  if (!isOpen) return null;

  // Mobile-responsive styles
  const backdropStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'flex-start', // Top-aligned to prevent centering issues on short viewports
    justifyContent: 'center',
    zIndex,
    padding: isMobile ? 0 : '2rem 1rem', // No padding on mobile
    overflowY: 'auto',
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: 'var(--background-color)',
    borderRadius: isMobile ? 0 : 'var(--radius)', // Edge-to-edge on mobile
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    width: '100%',
    maxWidth: isMobile ? '100%' : maxWidth, // Full-width on mobile
    maxHeight: isMobile ? '100dvh' : 'calc(100vh - 4rem)', // Full-height on mobile (dvh for Android nav bar)
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    margin: isMobile ? 0 : '0 auto', // No margin on mobile
  };

  const headerStyle: React.CSSProperties = {
    padding: isMobile ? '0.5rem 0.75rem' : '0.65rem 0.75rem',
    paddingTop: isMobile
      ? `calc(0.75rem + var(--safe-area-inset-top))` // Respect iOS notch
      : '0.75rem',
    borderBottom: '1px solid var(--border-color)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'var(--border-light)',
    borderTopLeftRadius: isMobile ? 0 : 'var(--radius)',
    borderTopRightRadius: isMobile ? 0 : 'var(--radius)',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
    margin: 0,
  };

  const closeButtonStyle: React.CSSProperties = {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '1.5rem',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    padding: '0.5rem',
    lineHeight: 1,
    borderRadius: 'var(--radius)',
    transition: 'background-color 0.2s, color 0.2s',
    minHeight: isMobile ? '40px' : 'auto', // Touch-friendly on mobile
    minWidth: isMobile ? '40px' : 'auto',
  };

  const contentStyle: React.CSSProperties = {
    padding: isMobile ? '1rem' : '1.5rem',
    paddingBottom: isMobile
      ? `calc(1rem + var(--safe-area-inset-bottom))` // Respect iOS home indicator
      : '1.5rem',
    overflowY: 'auto',
    flex: 1,
  };

  // Render modal in a portal to prevent z-index issues
  return createPortal(
    <div
      style={backdropStyle}
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      aria-label={!title ? ariaLabel || 'Dialog' : undefined}
    >
      <div
        ref={modalRef}
        style={modalStyle}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Render header if title or headerContent provided */}
        {(title || headerContent) && (
          <div style={headerStyle}>
            {/* Custom header content OR default title */}
            {headerContent || (
              <h2 id="modal-title" style={titleStyle}>
                {title}
              </h2>
            )}

            {/* Close button inside header */}
            {showCloseButton && (
              <button
                style={closeButtonStyle}
                onClick={handleClose}
                aria-label="Close modal"
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--surface-color)';
                  e.currentTarget.style.color = 'var(--text-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--text-secondary)';
                }}
              >
                <X size={20} aria-hidden="true" />
              </button>
            )}
          </div>
        )}

        {/* Floating close button when no header but showCloseButton=true */}
        {!title && !headerContent && showCloseButton && (
          <button
            style={{
              ...closeButtonStyle,
              position: 'absolute',
              top: isMobile ? 'calc(0.75rem + var(--safe-area-inset-top))' : '1rem',
              right: isMobile ? '0.75rem' : '1rem',
              zIndex: 1,
            }}
            onClick={handleClose}
            aria-label="Close modal"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--surface-color)';
              e.currentTarget.style.color = 'var(--text-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--text-secondary)';
            }}
          >
            <X size={20} aria-hidden="true" />
          </button>
        )}
        <div style={contentStyle} className="modal-scroll">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};
